import React, { Component, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { defineMessages } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import Content from '../../../common/components/configuration/view.tsx';
import type { EditableComponent } from '../../../common/model/types.tsx';
import { updateComponentRequest } from '../../../state/actions.tsx';
import { getBaseUrl } from '../../../state/context/selectors.tsx';
import {
	getProjectDefaultAssignee,
	getProjectLeadName,
} from '../../../state/project/selectors.tsx';
import type { DefaultAssignee as DefaultProjectAssignee } from '../../../state/project/types.tsx';
import type { State as AppState } from '../../../state/reducer.tsx';
import { updateEditedComponent, cancel as cancelEdit } from '../../../state/ui/edit/actions.tsx';
import {
	isEditing,
	saveIsInProgress,
	getEditedComponent,
	getNameErrorMessage,
} from '../../../state/ui/edit/selectors.tsx';

const spinnerContainerStyles = xcss({
	marginRight: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

const messages = defineMessages({
	header: {
		id: 'project-components.dialogs-edit-header',
		defaultMessage: 'Edit component',
		description: '',
	},
	saveButton: {
		id: 'project-components.dialogs-edit-save-button',
		defaultMessage: 'Save',
		description: '',
	},
	cancelButton: {
		id: 'project-components.dialogs-edit-cancel-button',
		defaultMessage: 'Cancel',
		description: '',
	},
});

export type Props = {
	intl: IntlShape;
	baseUrl: string;
	component: EditableComponent | undefined;
	onConfirm: () => void;
	onCancel: () => void;
	isDialogOpen: boolean;
	isSaving: boolean;
	onUpdateComponent: (component: EditableComponent) => void;
	projectDefaultAssignee?: DefaultProjectAssignee;
	projectLeadName?: string;
	nameErrorMessage?: string;
};

export type State = {
	isSaveDisabled: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class EditModal extends Component<Props, State> {
	state: State = { isSaveDisabled: false };

	onUpdateComponent = (component: EditableComponent): void => {
		const isSaveDisabled = !component.name || !component.name.trim();
		this.setState({ isSaveDisabled });
		this.props.onUpdateComponent(component);
	};

	// eslint-disable-next-line @atlaskit/design-system/use-heading
	renderHeader = () => <h2>{this.props.intl.formatMessage(messages.header)}</h2>;

	renderFooter = () => (
		<ButtonsContainer>
			{this.props.isSaving && (
				<Box xcss={spinnerContainerStyles}>
					<Spinner size="small" />
				</Box>
			)}
			<Button
				appearance="primary"
				type="submit"
				onClick={this.props.onConfirm}
				isDisabled={this.props.isSaving || this.state.isSaveDisabled}
			>
				{this.props.intl.formatMessage(messages.saveButton)}
			</Button>
			<Button
				appearance="subtle-link"
				onClick={this.props.onCancel}
				isDisabled={this.props.isSaving}
			>
				{this.props.intl.formatMessage(messages.cancelButton)}
			</Button>
		</ButtonsContainer>
	);

	render() {
		const {
			baseUrl,
			component,
			projectDefaultAssignee,
			projectLeadName,
			onCancel,
			nameErrorMessage,
			isDialogOpen,
		} = this.props;

		if (!isDialogOpen || !component) {
			return null;
		}

		return (
			<form>
				<ShortcutScope>
					<ModalDialog
						onClose={onCancel}
						width="medium"
						messageId="project-directories.components.modals.edit.modal-dialog"
						messageType="transactional"
					>
						<ModalHeader>{this.renderHeader()}</ModalHeader>
						<ModalBody>
							{}
							<Content
								baseUrl={baseUrl}
								name={component.name}
								description={component.description}
								lead={component.lead}
								defaultAssignee={component.defaultAssignee}
								onUpdateComponent={this.onUpdateComponent}
								projectDefaultAssignee={projectDefaultAssignee}
								projectLeadName={projectLeadName}
								nameErrorMessage={nameErrorMessage}
							/>
						</ModalBody>
						<ModalFooter>{this.renderFooter()}</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			</form>
		);
	}
}

type StateProps = Pick<
	Props,
	| 'baseUrl'
	| 'isDialogOpen'
	| 'isSaving'
	| 'projectDefaultAssignee'
	| 'projectLeadName'
	| 'component'
	| 'nameErrorMessage'
>;

const mapStateToProps = (state: AppState): StateProps => ({
	baseUrl: getBaseUrl(state),
	isDialogOpen: isEditing(state),
	isSaving: saveIsInProgress(state),
	projectDefaultAssignee: getProjectDefaultAssignee(state),
	projectLeadName: getProjectLeadName(state),
	component: getEditedComponent(state),
	nameErrorMessage: getNameErrorMessage(state),
});

type DispatchProps = Pick<Props, 'onCancel' | 'onConfirm' | 'onUpdateComponent'>;

const mapDispatchToProps: DispatchProps = {
	onCancel: cancelEdit,
	onConfirm: updateComponentRequest,
	onUpdateComponent: updateEditedComponent,
};

const Connected: ComponentType<{}> = connect(
	mapStateToProps,
	mapDispatchToProps,
)(injectIntl(EditModal));

export default Connected;
